/**
 * EventsCalendar — /calendar 동적 월간 캘린더 위젯.
 *
 * - 그리드·이벤트 카드를 클라이언트 렌더(SSR 데모 없음). 월/요일 라벨은 Intl(html lang).
 * - 헤더: 분리형 월 드롭다운 + 년 드롭다운(상호배타), 좌/우 화살표, Today 버튼, 이번 달 이벤트 수.
 * - 날짜 자동선택 없음 — 달 이동/로드 시 active 미지정(피드는 '날짜 선택' 안내). 클릭/Today 로만 선택.
 * - 날짜 상태: today(링) · has-events(점/카운트) · selected(채움) — CSS 클래스로 구분.
 * - 키보드 탐색(방향키·Enter·PageUp/Down·Home/End), URL 동기화(?month=YYYY-MM, replaceState).
 * - filter-sidebar(검색·카테고리·태그) 변경 → 캘린더만 AJAX 재로드.
 * - 카드 마크업은 뷰의 <template data-cal-card-tpl> 단일 소스 복제(#19). 정적 문구는 뷰 __() SSR.
 *
 * 마운트: UiSectionsController.init() → EventsCalendar.init().
 */

interface CalEvent {
    id: number;
    title: string;
    url: string;
    day: number;
    start: string | null;
    end: string | null;
    location: string;
    type: string;
}

const PICKER_YEAR_SPAN = 6; // 현재 연도 기준 ±N 년.

export class EventsCalendar {
    static init(): void {
        const root = document.querySelector<HTMLElement>('[data-section="events-calendar-interactive"]');
        if (root) {
            new EventsCalendar(root).boot();
        }
    }

    private readonly root: HTMLElement;
    private readonly cal: HTMLElement | null;
    private readonly eventsUrl: string;
    private readonly locale: string;

    private year: number;
    private month: number; // 1-12
    private selectedDay = 0;          // 0 = 선택 없음.
    private focusedDay = 1;           // roving tabindex 포커스 대상.
    private eventsByDay: Map<number, CalEvent[]> = new Map();
    private openDd: "month" | "year" | null = null;

    constructor(root: HTMLElement) {
        this.root = root;
        this.cal = root.querySelector<HTMLElement>(".sec-events-calendar-interactive__cal");
        this.eventsUrl = root.dataset.calEventsUrl || "";
        this.locale = root.dataset.calLocale || document.documentElement.lang || "en";

        // 초기 월 — URL ?month= 우선, 없으면 data-cal-initial-month, 없으면 현재월.
        const now = new Date();
        const urlMonth = new URLSearchParams(window.location.search).get("month");
        const src = (urlMonth && /^\d{4}-\d{2}$/.test(urlMonth) ? urlMonth : root.dataset.calInitialMonth) || "";
        const m = src.match(/^(\d{4})-(\d{2})$/);
        this.year = m ? Number(m[1]) : now.getFullYear();
        this.month = m ? Number(m[2]) : now.getMonth() + 1;
    }

    private boot(): void {
        this.renderWeekdays();
        this.buildMonthMenu();
        this.buildYearMenu();
        this.bindNav();
        this.bindDropdowns();
        this.bindToday();
        this.bindGridKeys();
        this.bindFilter();
        this.renderGrid();
        void this.loadMonth();
    }

    // ── i18n ────────────────────────────────────────────────────
    private t(key: string, replace?: Record<string, string | number>): string {
        const tFn = (window as unknown as { t?: (k: string, r?: Record<string, string | number>) => string }).t;
        return typeof tFn === "function" ? tFn(key, replace) : key;
    }

    // ── Intl 라벨 ────────────────────────────────────────────────
    private monthLong(month: number): string {
        return new Intl.DateTimeFormat(this.locale, { month: "long" }).format(new Date(2000, month - 1, 1));
    }

    private renderWeekdays(): void {
        const wrap = this.root.querySelector<HTMLElement>("[data-cal-weekdays]");
        if (!wrap) return;
        const fmt = new Intl.DateTimeFormat(this.locale, { weekday: "short" });
        const base = new Date(2024, 5, 2); // 2024-06-02 = 일요일.
        let html = "";
        for (let i = 0; i < 7; i++) {
            const d = new Date(base);
            d.setDate(base.getDate() + i);
            html += `<span>${this.esc(fmt.format(d))}</span>`;
        }
        wrap.innerHTML = html;
    }

    // ── 헤더 라벨 + 그리드 렌더 ──────────────────────────────────
    private renderHead(): void {
        const monthEl = this.root.querySelector<HTMLElement>("[data-cal-month-label]");
        if (monthEl) monthEl.textContent = this.monthLong(this.month);
        const yearEl = this.root.querySelector<HTMLElement>("[data-cal-year-label]");
        if (yearEl) yearEl.textContent = String(this.year);
        this.syncMenuSelection();
    }

    private renderGrid(): void {
        this.renderHead();
        const grid = this.root.querySelector<HTMLElement>("[data-cal-grid]");
        if (!grid) return;

        const firstWeekday = new Date(this.year, this.month - 1, 1).getDay(); // 0=Sun
        const daysInMonth = new Date(this.year, this.month, 0).getDate();
        const today = new Date();
        const isThisMonth = today.getFullYear() === this.year && today.getMonth() + 1 === this.month;
        const todayDate = today.getDate();

        if (this.focusedDay > daysInMonth) this.focusedDay = daysInMonth;
        if (this.focusedDay < 1) this.focusedDay = 1;

        let html = "";
        for (let i = 0; i < firstWeekday; i++) {
            html += `<span class="sec-events-calendar-interactive__day sec-events-calendar-interactive__day--empty" aria-hidden="true"></span>`;
        }
        for (let d = 1; d <= daysInMonth; d++) {
            const evs = this.eventsByDay.get(d);
            const cls = ["sec-events-calendar-interactive__day", "no-body-sm"];
            if (evs) cls.push("sec-events-calendar-interactive__day--has-events");
            if (isThisMonth && d === todayDate) cls.push("sec-events-calendar-interactive__day--today");
            const selected = d === this.selectedDay;
            if (selected) cls.push("is-selected");
            const tabindex = d === this.focusedDay ? "0" : "-1";
            const dot = evs ? `<span class="sec-events-calendar-interactive__day-dot" aria-hidden="true"></span>` : "";
            const count = evs && evs.length > 1
                ? `<span class="sec-events-calendar-interactive__day-count" aria-hidden="true">${evs.length}</span>`
                : "";
            const aria = evs ? ` aria-label="${d} · ${evs.length}"` : "";
            html += `<button type="button" class="${cls.join(" ")}" role="gridcell" data-cal-day="${d}" tabindex="${tabindex}" aria-pressed="${selected ? "true" : "false"}"${aria}>${d}${dot}${count}</button>`;
        }
        grid.innerHTML = html;

        grid.querySelectorAll<HTMLButtonElement>("[data-cal-day]").forEach((btn) => {
            btn.addEventListener("click", () => {
                this.focusedDay = Number(btn.dataset.calDay);
                this.selectDay(this.focusedDay);
            });
        });
    }

    // ── 데이터 로드 ──────────────────────────────────────────────
    private async loadMonth(): Promise<void> {
        if (!this.eventsUrl) {
            this.eventsByDay = new Map();
            this.afterLoad();
            return;
        }
        this.cal?.classList.add("is-loading");
        try {
            const params = new URLSearchParams({ month: `${this.year}-${String(this.month).padStart(2, "0")}` });
            const f = this.filterValues();
            if (f.categoryId) params.set("category_id", f.categoryId);
            if (f.tagId) params.set("tag_id", f.tagId);
            if (f.search) params.set("search", f.search);

            const sep = this.eventsUrl.includes("?") ? "&" : "?";
            const res = await fetch(`${this.eventsUrl}${sep}${params.toString()}`, {
                method: "GET",
                credentials: "same-origin",
                headers: { "X-Requested-With": "fetch" },
            });
            const data = await res.json();
            const events: CalEvent[] = Array.isArray(data?.events) ? data.events : [];
            this.eventsByDay = new Map();
            for (const ev of events) {
                const day = Number(ev.day);
                if (!day) continue;
                if (!this.eventsByDay.has(day)) this.eventsByDay.set(day, []);
                this.eventsByDay.get(day)!.push(ev);
            }
            // 자동선택 없음 — 선택은 사용자 클릭/Today 로만.
        } catch {
            this.eventsByDay = new Map();
        } finally {
            this.cal?.classList.remove("is-loading");
            this.afterLoad();
        }
    }

    private afterLoad(): void {
        this.renderGrid();
        this.renderCount();
        this.renderFeed();
    }

    private renderCount(): void {
        const el = this.root.querySelector<HTMLElement>("[data-cal-count]");
        if (!el) return;
        let total = 0;
        this.eventsByDay.forEach((list) => { total += list.length; });
        const key = total === 0 ? "calendar.events_count_zero" : total === 1 ? "calendar.events_count_one" : "calendar.events_count";
        el.textContent = this.t(key, { n: total });
    }

    // ── 선택 / 피드 ──────────────────────────────────────────────
    private selectDay(day: number): void {
        this.selectedDay = day;
        this.root.querySelectorAll<HTMLButtonElement>("[data-cal-day]").forEach((b) => {
            const dd = Number(b.dataset.calDay);
            const on = dd === day;
            b.classList.toggle("is-selected", on);
            b.setAttribute("aria-pressed", on ? "true" : "false");
            b.tabIndex = dd === this.focusedDay ? 0 : -1;
        });
        this.renderFeed();
    }

    private renderFeed(): void {
        const feed = this.root.querySelector<HTMLElement>("[data-cal-feed]");
        const empty = this.root.querySelector<HTMLElement>("[data-cal-empty]");
        const prompt = this.root.querySelector<HTMLElement>("[data-cal-prompt]");
        const tpl = this.root.querySelector<HTMLTemplateElement>("[data-cal-card-tpl]");
        if (!feed) return;

        feed.innerHTML = "";

        // 선택 없음 → '날짜 선택' 안내(빈 상태와 별개).
        if (!this.selectedDay) {
            feed.hidden = true;
            if (empty) empty.hidden = true;
            if (prompt) prompt.hidden = false;
            return;
        }
        if (prompt) prompt.hidden = true;

        const events = this.eventsByDay.get(this.selectedDay) ?? [];
        if (!events.length || !tpl) {
            feed.hidden = true;
            if (empty) empty.hidden = false;
            return;
        }
        feed.hidden = false;
        if (empty) empty.hidden = true;

        for (const ev of events) {
            const node = tpl.content.firstElementChild?.cloneNode(true) as HTMLElement | null;
            if (!node) continue;
            this.fillCard(node, ev);
            feed.appendChild(node);
        }
    }

    private fillCard(node: HTMLElement, ev: CalEvent): void {
        const titleEl = node.querySelector<HTMLElement>("[data-card-title]");
        if (titleEl) titleEl.textContent = ev.title;

        const badge = node.querySelector<HTMLElement>("[data-card-badge]");
        if (badge) {
            if (ev.type) { badge.textContent = ev.type; badge.hidden = false; }
            else { badge.hidden = true; }
        }

        const time = node.querySelector<HTMLElement>("[data-card-time]");
        const timeText = node.querySelector<HTMLElement>("[data-card-time-text]");
        const timeStr = this.formatRange(ev.start, ev.end);
        if (time && timeText) {
            if (timeStr) { timeText.textContent = timeStr; time.hidden = false; }
            else { time.hidden = true; }
        }

        const place = node.querySelector<HTMLElement>("[data-card-place]");
        const placeText = node.querySelector<HTMLElement>("[data-card-place-text]");
        if (place && placeText) {
            if (ev.location) { placeText.textContent = ev.location; place.hidden = false; }
            else { place.hidden = true; }
        }

        const link = node.querySelector<HTMLAnchorElement>("[data-card-link]");
        if (link) link.href = ev.url || "#";
    }

    private formatRange(start: string | null, end: string | null): string {
        if (!start) return "";
        const fmt = (s: string): string =>
            new Intl.DateTimeFormat(this.locale, { month: "short", day: "numeric" }).format(new Date(s + "T00:00:00"));
        if (end && end !== start) return `${fmt(start)} – ${fmt(end)}`;
        return fmt(start);
    }

    // ── 월 이동 ──────────────────────────────────────────────────
    private goTo(year: number, month: number, opts: { selectDay?: number } = {}): void {
        this.year = year;
        this.month = month;
        this.selectedDay = opts.selectDay ?? 0;   // 기본: 자동선택 없음.
        if (this.focusedDay < 1) this.focusedDay = 1;
        this.syncUrl();
        this.renderGrid();
        void this.loadMonth();
    }

    private shiftMonth(delta: number): void {
        let m = this.month + delta;
        let y = this.year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        this.goTo(y, m);
    }

    private goToday(): void {
        const now = new Date();
        this.focusedDay = now.getDate();
        this.goTo(now.getFullYear(), now.getMonth() + 1, { selectDay: now.getDate() });
    }

    private bindNav(): void {
        this.root.querySelector<HTMLButtonElement>("[data-cal-prev]")?.addEventListener("click", () => this.shiftMonth(-1));
        this.root.querySelector<HTMLButtonElement>("[data-cal-next]")?.addEventListener("click", () => this.shiftMonth(1));
    }

    private bindToday(): void {
        this.root.querySelector<HTMLButtonElement>("[data-cal-today]")?.addEventListener("click", () => this.goToday());
    }

    private syncUrl(): void {
        if (!window.history?.replaceState) return;
        const params = new URLSearchParams(window.location.search);
        params.set("month", `${this.year}-${String(this.month).padStart(2, "0")}`);
        window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
    }

    // ── 분리형 월/년 드롭다운 ───────────────────────────────────
    private buildMonthMenu(): void {
        const menu = this.root.querySelector<HTMLElement>("[data-cal-month-menu]");
        if (!menu) return;
        let html = "";
        for (let m = 1; m <= 12; m++) {
            html += `<button type="button" role="option" class="sec-events-calendar-interactive__dd-opt" data-cal-pick-month="${m}">${this.esc(this.monthLong(m))}</button>`;
        }
        menu.innerHTML = html;
        menu.querySelectorAll<HTMLButtonElement>("[data-cal-pick-month]").forEach((b) => {
            b.addEventListener("click", () => {
                this.openDropdown(null);
                this.goTo(this.year, Number(b.dataset.calPickMonth));
            });
        });
    }

    private buildYearMenu(): void {
        const menu = this.root.querySelector<HTMLElement>("[data-cal-year-menu]");
        if (!menu) return;
        const cur = new Date().getFullYear();
        let html = "";
        for (let y = cur - PICKER_YEAR_SPAN; y <= cur + PICKER_YEAR_SPAN; y++) {
            html += `<button type="button" role="option" class="sec-events-calendar-interactive__dd-opt" data-cal-pick-year="${y}">${y}</button>`;
        }
        menu.innerHTML = html;
        menu.querySelectorAll<HTMLButtonElement>("[data-cal-pick-year]").forEach((b) => {
            b.addEventListener("click", () => {
                this.openDropdown(null);
                this.goTo(Number(b.dataset.calPickYear), this.month);
            });
        });
    }

    private bindDropdowns(): void {
        const monthTrig = this.root.querySelector<HTMLButtonElement>("[data-cal-month-trigger]");
        const yearTrig = this.root.querySelector<HTMLButtonElement>("[data-cal-year-trigger]");
        monthTrig?.addEventListener("click", (e) => { e.stopPropagation(); this.openDropdown(this.openDd === "month" ? null : "month"); });
        yearTrig?.addEventListener("click", (e) => { e.stopPropagation(); this.openDropdown(this.openDd === "year" ? null : "year"); });
        // 메뉴 내부 클릭은 닫힘 방지(옵션 핸들러가 직접 닫음).
        this.root.querySelector<HTMLElement>("[data-cal-month-menu]")?.addEventListener("click", (e) => e.stopPropagation());
        this.root.querySelector<HTMLElement>("[data-cal-year-menu]")?.addEventListener("click", (e) => e.stopPropagation());
        document.addEventListener("click", () => this.openDropdown(null));
        document.addEventListener("keydown", (e) => { if (e.key === "Escape") this.openDropdown(null); });
    }

    private openDropdown(which: "month" | "year" | null): void {
        this.openDd = which;
        const set = (dd: "month" | "year") => {
            const trig = this.root.querySelector<HTMLButtonElement>(`[data-cal-${dd}-trigger]`);
            const menu = this.root.querySelector<HTMLElement>(`[data-cal-${dd}-menu]`);
            const on = which === dd;
            if (menu) menu.hidden = !on;
            trig?.setAttribute("aria-expanded", on ? "true" : "false");
        };
        set("month");
        set("year");
        if (which) this.syncMenuSelection();
    }

    private syncMenuSelection(): void {
        this.root.querySelectorAll<HTMLButtonElement>("[data-cal-pick-month]").forEach((b) => {
            b.classList.toggle("is-selected", Number(b.dataset.calPickMonth) === this.month);
        });
        this.root.querySelectorAll<HTMLButtonElement>("[data-cal-pick-year]").forEach((b) => {
            b.classList.toggle("is-selected", Number(b.dataset.calPickYear) === this.year);
        });
    }

    // ── 키보드 탐색(roving tabindex) ────────────────────────────
    private bindGridKeys(): void {
        const grid = this.root.querySelector<HTMLElement>("[data-cal-grid]");
        if (!grid) return;
        grid.addEventListener("keydown", (e) => {
            const daysInMonth = new Date(this.year, this.month, 0).getDate();
            let handled = true;
            switch (e.key) {
                case "ArrowRight": this.moveFocus(1, daysInMonth); break;
                case "ArrowLeft":  this.moveFocus(-1, daysInMonth); break;
                case "ArrowDown":  this.moveFocus(7, daysInMonth); break;
                case "ArrowUp":    this.moveFocus(-7, daysInMonth); break;
                case "Home":       this.focusDay(1); break;
                case "End":        this.focusDay(daysInMonth); break;
                case "PageUp":     this.shiftMonth(-1); break;
                case "PageDown":   this.shiftMonth(1); break;
                case "Enter":
                case " ":          this.selectDay(this.focusedDay); break;
                default: handled = false;
            }
            if (handled) e.preventDefault();
        });
    }

    private moveFocus(delta: number, daysInMonth: number): void {
        const next = this.focusedDay + delta;
        if (next < 1 || next > daysInMonth) return; // 이번 달 안에서만(월 넘김은 PageUp/Down).
        this.focusDay(next);
    }

    private focusDay(day: number): void {
        this.focusedDay = day;
        this.root.querySelectorAll<HTMLButtonElement>("[data-cal-day]").forEach((b) => {
            const on = Number(b.dataset.calDay) === day;
            b.tabIndex = on ? 0 : -1;
            if (on) b.focus();
        });
    }

    // ── aside 필터 연동 ─────────────────────────────────────────
    private filterValues(): { categoryId: string; tagId: string; search: string } {
        const form = this.root.querySelector<HTMLFormElement>("form[data-news-filter]");
        if (!form) return { categoryId: "", tagId: "", search: "" };
        const get = (name: string): string => {
            const el = form.querySelector<HTMLInputElement>(`[name="${name}"]:checked, [name="${name}"]:not([type=radio]):not([type=checkbox])`);
            return el ? el.value : "";
        };
        const cat = get("category_id");
        const tag = get("tag_id");
        const search = (form.querySelector<HTMLInputElement>('[name="search"]')?.value || "").trim();
        return {
            categoryId: cat && cat !== "0" ? cat : "",
            tagId: tag && tag !== "0" ? tag : "",
            search,
        };
    }

    private bindFilter(): void {
        const form = this.root.querySelector<HTMLFormElement>("form[data-news-filter]");
        if (!form) return;
        // Application.initNewsFilter 가 change 시 requestSubmit() → submit 가로채 AJAX 로 대체.
        form.addEventListener("submit", (e) => {
            e.preventDefault();
            this.selectedDay = 0;
            void this.loadMonth();
        });
    }

    private esc(s: string): string {
        const d = document.createElement("div");
        d.textContent = s;
        return d.innerHTML;
    }
}
